import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useLocale } from 'src/contexts/LocaleContext'
import { graphQLClient } from 'src/data/graphql/graphql-client'
import { CartLinesAddDocument, CartLinesUpdateDocument } from 'src/data/graphql-generated'
import {
  createMatchingDonationWithUpdatedSellingPlan,
  createMatchingNewQuantityDonation,
  initialiseCartLinesUpdateArray,
} from 'src/functions/cart/cartLinesUpdateHelpers'
import { onCartMutationSuccess } from 'src/functions/cart/onCartMutationSuccess'
import { useCartQuery } from 'src/hooks/useCartQuery'

import type { Cart, CartLineUpdateInput, UserError, CartLine, CartWarning } from 'src/data/graphql-generated'

export type CartLinesUpdateMutationFn = (variables: {
  item: CartLine
  newQuantity?: number
  currentSellingPlanInterval?: number
  newSellingPlanInterval?: number
}) => Promise<{ cart: Cart; warnings: CartWarning[]; userErrors: UserError[] }>

export const useCartLinesUpdateMutation = () => {
  const queryClient = useQueryClient()
  const { langCode } = useLocale()
  const { data: currentCart } = useCartQuery()

  const mutationFn: CartLinesUpdateMutationFn = async (variables) => {
    const { item, newQuantity, currentSellingPlanInterval, newSellingPlanInterval } = variables

    const initialisedUpdateArray: CartLineUpdateInput[] = initialiseCartLinesUpdateArray({
      item,
      newQuantity,
      newSellingPlanInterval,
    })

    const possibleDonationUpdate = createMatchingNewQuantityDonation({
      itemCartLine: item,
      currentCartLines: currentCart?.lines.nodes || [],
      currentSellingPlanInterval,
      newQuantity,
    })

    const { toAdd: possibleDonationWithSellingPlanToAdd, toUpdate: possibleDonationWithSellingPlanToUpdate } =
      createMatchingDonationWithUpdatedSellingPlan({
        itemCartLine: item,
        currentCartLines: currentCart?.lines.nodes || [],
        currentSellingPlanInterval,
        newSellingPlanInterval,
      })

    const updatingCartLines = [
      ...initialisedUpdateArray,
      ...[possibleDonationUpdate, possibleDonationWithSellingPlanToUpdate].filter(Boolean).flat(),
    ]

    if (possibleDonationWithSellingPlanToAdd) {
      await graphQLClient.request(CartLinesAddDocument, {
        cartId: currentCart?.id,
        lines: possibleDonationWithSellingPlanToAdd,
        lang: langCode,
      })
    }

    const { cartLinesUpdate }: { cartLinesUpdate: { cart: Cart; warnings: CartWarning[]; userErrors: UserError[] } } =
      await graphQLClient.request(CartLinesUpdateDocument, {
        cartId: currentCart?.id,
        lines: updatingCartLines,
        lang: langCode,
      })

    return cartLinesUpdate
  }

  return useMutation({
    mutationFn,
    onSuccess: ({ cart, userErrors }) => onCartMutationSuccess(queryClient, cart, userErrors),
  })
}
